import { IkeAngle, IkeGeodesic } from '@ikegps/core-measurement';
import { action, computed, observable, makeObservable } from 'mobx';
import type { MeasurementPoint } from './measurement.state';

export class DistanceMeasureState {
	@observable points: MeasurementPoint[] = [];
	@observable isActive: boolean = false;

	constructor() {
		makeObservable(this);
	}

	@action.bound
	setActive(active: boolean) {
		this.isActive = active;
		if (!active) {
			this.clearPoints();
		}
	}

	/**
	 * Add a measurement point. If two points already exist, clear them first
	 * so the new click becomes the start of a fresh measurement.
	 */
	@action.bound
	addPoint(point: MeasurementPoint) {
		if (this.points.length >= 2) {
			this.points = [];
		}
		this.points.push(point);
	}

	@action.bound
	updatePointLocation(id: string, location: google.maps.LatLng) {
		const point = this.points.find((p) => p.id === id);
		if (point) {
			point.location = location;
		}
	}

	@action.bound
	clearPoints() {
		this.points = [];
	}

	@action.bound
	reset() {
		this.points = [];
		this.isActive = false;
	}

	/** Only call when points.length >= 2 */
	private getVector() {
		const a = this.points[0]!;
		const b = this.points[1]!;
		return IkeGeodesic.getVector(
			{ latitude: a.location.lat(), longitude: a.location.lng(), altitude: 0 },
			{ latitude: b.location.lat(), longitude: b.location.lng(), altitude: 0 }
		);
	}

	/** Distance between the two points in meters, or 0 if fewer than 2 points */
	@computed
	get distance(): number {
		if (this.points.length < 2) {
			return 0;
		}
		return this.getVector().distance;
	}

	/** Bearing from point A to point B in degrees (0–360), or 0 if fewer than 2 points */
	@computed
	get bearing(): number {
		if (this.points.length < 2) {
			return 0;
		}
		return IkeAngle.toDegrees(this.getVector().bearing);
	}
}
